import { useState, useContext } from "react";
import {
  deleteCategory,
  updateCategoryDetails,
} from "../../../services/inventory";
import { ToastContext, RequestLoader } from "../../../hooks/context";
import { useMutation } from "react-query";
import { cleanObject, getRandomIntInclusive } from "../../../helpers";
import { useCreateCategory } from "../../../hooks/api/mutations/useInventory";

const ProductCategoryModalLogic = ({
  setIsActive,
  category,
  refetchCategories = () => {},
  isEdit,
}) => {
  const [categoryName, setCategoryName] = useState(
    category ? category?.name : "",
  );
  const [subCategories, setSubCategories] = useState(
    category.sub_categories
      ? category.sub_categories.map((sub) => ({
          id: sub.id || Date.now() + Math.random(),
          value: sub.name || "",
        }))
      : [],
  );
  const existingIds = category?.sub_categories?.map((s) => String(s.id)) || [];
  const triggerToast = useContext(ToastContext);
  const { setRequestLoaderProgress } = useContext(RequestLoader);

  const {
    mutate: addProductCategories,
    isLoading: isCreatingProductCategories,
  } = useCreateCategory();

  const {
    mutate: updateProductCategories,
    isLoading: isEditingProductCategories,
  } = useMutation((data) => updateCategoryDetails(data));

  const handleAddCategory = () => {
    setRequestLoaderProgress(getRandomIntInclusive(10, 50));
    addProductCategories(
      cleanObject({
        name: categoryName,
        sub_categories: subCategories
          ?.filter((sub) => sub.trim())
          .map((sub) => ({ name: sub })),
      }),
      {
        onSuccess: () => {
          refetchCategories();
          triggerToast("Product category added successfully", "success");
          setIsActive(false);
        },
        onSettled: () => {
          setRequestLoaderProgress(100);
        },
      },
    );
  };

  const handleAddSubCategory = () => {
    if (isEdit) {
      setSubCategories((prev) => [
        ...prev,
        { id: Date.now().toString(), value: "" },
      ]);
    } else {
      setSubCategories((prev) => [...prev, ""]);
    }
  };

  const handleDeleteSubCategory = (index) => {
    setSubCategories((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteProductCategory = (id) => {
    deleteCategory(id)
      .then(() => {
        triggerToast("Product sub category deleted successfully", "success");
      })
      .catch((error) => {});
  };

  const handleSubCategoryChange = (id, newValue) => {
    if (isEdit) {
      setSubCategories((prev) =>
        prev.map((sub) => (sub.id === id ? { ...sub, value: newValue } : sub)),
      );
    } else {
      setSubCategories((prev) =>
        prev.map((sub, i) => (i === id ? newValue : sub)),
      );
    }
  };

  const handleUpdateCategory = () => {
    setRequestLoaderProgress(getRandomIntInclusive(10, 50));
    const subCategoryValues = subCategories
      .filter((sub) => sub.value?.trim())
      .map((sub) => {
        const payload = { name: sub.value.trim() };

        if (sub.id && existingIds.includes(String(sub.id))) {
          payload.id = sub.id;
        }

        return payload;
      });

    const data = cleanObject({
      name: categoryName,
      sub_categories: subCategoryValues,
    });
    updateProductCategories(
      { data, id: category.id },
      {
        onSuccess: () => {
          setRequestLoaderProgress(100);
          refetchCategories();
          setIsActive(false);
          triggerToast("Product category updated successfully", "success");
        },
      },
    );
  };

  return {
    categoryName,
    existingIds,
    setCategoryName,
    handleAddCategory,
    subCategories,
    setSubCategories,
    handleAddSubCategory,
    handleDeleteSubCategory,
    handleSubCategoryChange,
    handleUpdateCategory,
    isEditingProductCategories,
    isCreatingProductCategories,
    handleDeleteProductCategory,
  };
};

export default ProductCategoryModalLogic;
